import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  UserIcon, 
  DocumentTextIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import ShareRequestModal from './ShareRequestModal';

interface Lead {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  company?: string;
  leadUid?: string;
}

interface DocumentType {
  id: number;
  name: string;
  description?: string;
  isRequired: boolean;
}

interface DocumentTypeGroup {
  id: number;
  name: string;
  description?: string;
  color: string;
  document_types: { id: number; name: string }[];
}

interface RequestDocumentsModalProps {
  onClose: () => void;
  onRequestCreated: () => void;
  preSelectedLeadId?: number | string;
  lockLeadSelection?: boolean;
  preSelectedDocumentTypeIds?: number[];
}

const RequestDocumentsModal: React.FC<RequestDocumentsModalProps> = ({ 
  onClose, 
  onRequestCreated,
  preSelectedLeadId,
  lockLeadSelection = false,
  preSelectedDocumentTypeIds
}) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [documentGroups, setDocumentGroups] = useState<DocumentTypeGroup[]>([]);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<number[]>([]);
  const [note, setNote] = useState('');
  const [leadSearchTerm, setLeadSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [createdRequest, setCreatedRequest] = useState<{
    token: string;
    leadName: string;
    leadEmail: string;
    documentTypes: string[];
  } | null>(null);

  useEffect(() => {
    fetchLeads();
    fetchDocumentTypes();
    fetchDocumentGroups();
  }, []);

  // Pre-select lead if preSelectedLeadId is provided
  useEffect(() => {
    if (preSelectedLeadId && leads.length > 0) {
      const leadIdNum = typeof preSelectedLeadId === 'string' ? parseInt(preSelectedLeadId) : preSelectedLeadId;
      const lead = leads.find(l => l.id === leadIdNum);
      if (lead) {
        setSelectedLead(lead);
      }
    }
  }, [preSelectedLeadId, leads]);

  // Pre-select document types if preSelectedDocumentTypeIds is provided
  useEffect(() => {
    if (preSelectedDocumentTypeIds && preSelectedDocumentTypeIds.length > 0 && documentTypes.length > 0) {
      // Only select document types that exist in the available list
      const validTypeIds = preSelectedDocumentTypeIds.filter(
        typeId => documentTypes.some(dt => dt.id === typeId)
      );
      if (validTypeIds.length > 0) {
        setSelectedDocumentTypes(validTypeIds);
      }
    }
  }, [preSelectedDocumentTypeIds, documentTypes]);

  const fetchLeads = async () => {
    try {
      const response = await import('../services/api').then(module => 
        module.leadsAPI.getLeads()
      );
      setLeads(response.data || []);
    } catch (err: any) {
      console.error('Error fetching leads:', err);
      setError('Failed to fetch leads');
    }
  };

const fetchDocumentTypes = async () => {
    try {
      const response = await import('../services/api').then(module =>
        module.default.get('/documents/types')
      );
      setDocumentTypes(response.data.data || []);
    } catch (err: any) {
      console.error('Error fetching document types:', err);
      setError('Failed to fetch document types');
    }
  };

  const fetchDocumentGroups = async () => {
    try {
      const response = await import('../services/api').then(module =>
        module.default.get('/documents/groups')
      );
      setDocumentGroups(response.data.data || []);
    } catch (err: any) {
      console.error('Error fetching document groups:', err);
    }
  };

  const handleGroupSelect = (group: DocumentTypeGroup) => {
    const groupDocTypeIds = group.document_types.map(dt => dt.id);
    setSelectedDocumentTypes(prev => {
      const newIds = new Set(prev);
      groupDocTypeIds.forEach(id => newIds.add(id));
      return Array.from(newIds);
    });
  };

  const handleGroupDeselect = (group: DocumentTypeGroup) => {
    const groupDocTypeIds = new Set(group.document_types.map(dt => dt.id));
    setSelectedDocumentTypes(prev => prev.filter(id => !groupDocTypeIds.has(id)));
  };

  const isGroupFullySelected = (group: DocumentTypeGroup): boolean => {
    return group.document_types.every(dt => selectedDocumentTypes.includes(dt.id));
  };

  const isGroupPartiallySelected = (group: DocumentTypeGroup): boolean => {
    const selected = group.document_types.filter(dt => selectedDocumentTypes.includes(dt.id));
    return selected.length > 0 && selected.length < group.document_types.length;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedLead || selectedDocumentTypes.length === 0) {
      setError('Please select a lead and at least one document type');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await import('../services/api').then(module => 
        module.default.post('/documents/requests', {
          leadId: selectedLead.id,
          documentTypeIds: selectedDocumentTypes,
          note: note.trim() || undefined
        })
      );

      if (response.data.success) {
        // Get the created request data
        const requestData = response.data.data;
        const selectedDocTypes = documentTypes.filter(dt => selectedDocumentTypes.includes(dt.id));
        
        // Set the created request data for sharing
        setCreatedRequest({
          token: requestData.requestToken,
          leadName: `${selectedLead.firstName} ${selectedLead.lastName}`,
          leadEmail: selectedLead.email,
          documentTypes: selectedDocTypes.map(dt => dt.name)
        });
        
        // Show the share modal
        setShowShareModal(true);
      } else {
        throw new Error(response.data.message || 'Failed to create document request');
      }
    } catch (err: any) {
      console.error('Error creating document request:', err);
      setError(err.response?.data?.message || 'Failed to create document request');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentTypeToggle = (documentTypeId: number) => {
    setSelectedDocumentTypes(prev => 
      prev.includes(documentTypeId)
        ? prev.filter(id => id !== documentTypeId)
        : [...prev, documentTypeId]
    );
  };

  const filteredLeads = leads.filter(lead => {
    const searchLower = leadSearchTerm.toLowerCase();
    const fullName = `${lead.firstName} ${lead.lastName}`.toLowerCase();
    const email = lead.email.toLowerCase();
    const company = lead.company?.toLowerCase() || '';
    const leadUid = lead.leadUid?.toLowerCase() || '';
    
    return fullName.includes(searchLower) || 
           email.includes(searchLower) || 
           company.includes(searchLower) ||
           leadUid.includes(searchLower);
  });

  const selectedDocumentTypesData = documentTypes.filter(dt => 
    selectedDocumentTypes.includes(dt.id)
  );

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">Request Documents</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Lead Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Lead *
            </label>
            {!lockLeadSelection ? (
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search leads by UID, name, email, or company..."
                  value={leadSearchTerm}
                  onChange={(e) => setLeadSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            ) : (
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                <p className="text-sm text-gray-600">Lead selection is locked for this context.</p>
              </div>
            )}
            
            {!lockLeadSelection && leadSearchTerm && (
              <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md">
                {filteredLeads.length === 0 ? (
                  <div className="p-3 text-sm text-gray-500">No leads found</div>
                ) : (
                  filteredLeads.map((lead) => (
                    <button
                      key={lead.id}
                      type="button"
                      onClick={() => {
                        setSelectedLead(lead);
                        setLeadSearchTerm('');
                      }}
                      className="w-full text-left p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <UserIcon className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <div className="text-sm font-medium text-gray-900">
                              {lead.firstName} {lead.lastName}
                            </div>
                            {lead.leadUid && (
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                {lead.leadUid}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{lead.email}</div>
                          {lead.company && (
                            <div className="text-xs text-gray-400">{lead.company}</div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}

            {selectedLead && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <UserIcon className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <div className="text-sm font-medium text-blue-900">
                        {selectedLead.firstName} {selectedLead.lastName}
                      </div>
                      {selectedLead.leadUid && (
                        <span className="text-xs text-blue-700 bg-blue-100 px-2 py-0.5 rounded">
                          {selectedLead.leadUid}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-blue-700">{selectedLead.email}</div>
                    {selectedLead.company && (
                      <div className="text-xs text-blue-600">{selectedLead.company}</div>
                    )}
                  </div>
                  {!lockLeadSelection && (
                    <button
                      type="button"
                      onClick={() => setSelectedLead(null)}
                      className="ml-auto text-blue-600 hover:text-blue-800"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Document Types Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Document Types *
            </label>
            
            {/* Quick Select Groups */}
            {documentGroups.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-1.5">
                <span className="text-xs text-gray-500 mr-1">Quick select:</span>
                {documentGroups.map(group => {
                  const isFullySelected = isGroupFullySelected(group);
                  const isPartiallySelected = isGroupPartiallySelected(group);
                  return (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => isFullySelected ? handleGroupDeselect(group) : handleGroupSelect(group)}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition ${
                        isFullySelected 
                          ? 'bg-blue-600 text-white' 
                          : isPartiallySelected
                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      style={!isFullySelected && !isPartiallySelected ? { borderLeft: `3px solid ${group.color}` } : {}}
                    >
                      {group.name} ({group.document_types.length})
                    </button>
                  );
                })}
              </div>
            )}
            
            <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3">
              {documentTypes.length === 0 ? (
                <div className="text-sm text-gray-500">No document types available</div>
              ) : (
                documentTypes.map((docType) => (
                  <label key={docType.id} className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedDocumentTypes.includes(docType.id)}
                      onChange={() => handleDocumentTypeToggle(docType.id)}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900">
                          {docType.name}
                        </span>
                        {docType.isRequired && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Required
                          </span>
                        )}
                      </div>
                      {docType.description && (
                        <div className="text-sm text-gray-500">{docType.description}</div>
                      )}
                    </div>
                  </label>
                ))
              )}
            </div>

            {selectedDocumentTypesData.length > 0 && (
              <div className="mt-3">
                <div className="text-sm font-medium text-gray-700 mb-2">Selected Document Types:</div>
                <div className="flex flex-wrap gap-2">
                  {selectedDocumentTypesData.map((docType) => (
                    <span
                      key={docType.id}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      <DocumentTextIcon className="w-3 h-3 mr-1" />
                      {docType.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note (Optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add any additional instructions or context for this document request..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedLead || selectedDocumentTypes.length === 0}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating Request...
                </div>
              ) : (
                'Create Request'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Share Request Modal */}
      {showShareModal && createdRequest && (
        <ShareRequestModal
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            setCreatedRequest(null);
            onRequestCreated(); // Refresh the requests list
          }}
          requestToken={createdRequest.token}
          leadName={createdRequest.leadName}
          leadEmail={createdRequest.leadEmail}
          documentTypes={createdRequest.documentTypes}
        />
      )}
    </div>
  );
};

export default RequestDocumentsModal;
